/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import { formatIsoDate, parseIsoDate } from '@shared/vueltoDateTime';
import type { CashSessionRow } from '../../infra/cashSessionRow';
import type { CashHistorySort } from '../cashList.constants';
import type { HistorySection } from './cashManagement.types';
import { formatMoney } from './cashFormatMoney';
export { formatMoney };

/** Fondo inicial + cobros en efectivo del turno (+ ingresos manuales / − egresos si aplica). */
export function formatCashDrawerBreakdown(
  openingAmount: number,
  sessionCashSales: number,
  options?: { outflowsTotal?: number; showOutflows?: boolean; inflowsTotal?: number; showInflows?: boolean },
): string {
  const outflows = options?.outflowsTotal ?? 0;
  const inflows = options?.inflowsTotal ?? 0;
  const outflowsPart =
    options?.showOutflows && outflows > 0 ? ` − ${formatMoney(outflows)} egresos` : '';
  const inflowsPart =
    options?.showInflows && inflows > 0 ? ` + ${formatMoney(inflows)} ingresos` : '';
  const salesPart =
    sessionCashSales > 0 ? ` + ${formatMoney(sessionCashSales)} ventas efectivo` : '';
  return `${formatMoney(openingAmount)} fondo${salesPart}${inflowsPart}${outflowsPart}`;
}

/** Cantidad de ventas — mismo vocabulario que Informes y POS. */
export function formatSalesCount(n: number): string {
  return n === 1 ? '1 venta' : `${n} ventas`;
}

/** Movimientos de actividad de caja (feed sucursal). */
export function formatMovementCount(n: number): string {
  return n === 1 ? '1 movimiento' : `${n} movimientos`;
}

/** Resultado de arqueo al cerrar caja (modal de cierre). */
export function formatCashArqueoResult(
  difference: number,
  options?: { expectedNegative?: boolean },
): string {
  if (difference === 0) return 'Cuadra';
  const abs = formatMoney(Math.abs(difference));
  if (options?.expectedNegative && difference > 0) {
    return `Contra lo esperado: +${abs}`;
  }
  if (difference > 0) return `Sobrante ${abs}`;
  return `Faltante ${abs}`;
}

/** Diferencia inline en teasers y subtítulos (prefijo · incluido). */
export function formatCashDifferenceInline(difference: number): string {
  if (difference === 0) return ' · Sin diferencia';
  const sign = difference > 0 ? '+' : '';
  return ` · Diferencia ${sign}${formatMoney(difference)}`;
}

function formatSectionDayTitle(d: Date): string {
  return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Secciones del historial de cierres.
 * `closed_desc` (default): agrupa por día, turno abierto primero dentro del día.
 * `shortage_desc` / `surplus_desc`: lista plana ordenada por diferencia
 * (faltante más grande / sobrante más grande primero); el turno abierto queda
 * arriba porque no tiene diferencia.
 */
export function buildHistorySections(
  rows: CashSessionRow[],
  sort: CashHistorySort = 'closed_desc'
): HistorySection[] {
  if (sort !== 'closed_desc') {
    // shortage = diferencia más negativa primero (ASC); surplus = más positiva (DESC).
    const dir = sort === 'shortage_desc' ? 1 : -1;
    const sorted = [...rows].sort((a, b) => {
      const openA = a.status === 'open' ? 1 : 0;
      const openB = b.status === 'open' ? 1 : 0;
      if (openA !== openB) return openB - openA;
      const da = a.difference ?? 0;
      const db = b.difference ?? 0;
      if (da !== db) return (da - db) * dir;
      const ta = a.closedAt ?? a.openedAt ?? '';
      const tb = b.closedAt ?? b.openedAt ?? '';
      return tb.localeCompare(ta);
    });
    return [{ title: '', data: sorted, isFirstSection: true }];
  }

  const byDay = new Map<string, CashSessionRow[]>();
  for (const row of rows) {
    const anchor = row.closedAt ?? (row.status === 'open' ? row.openedAt : null);
    if (!anchor) continue;
    const key = formatIsoDate(new Date(anchor));
    const arr = byDay.get(key);
    if (arr) arr.push(row);
    else byDay.set(key, [row]);
  }
  const keys = [...byDay.keys()].sort((a, b) => b.localeCompare(a));
  const now = new Date();
  const todayKey = formatIsoDate(now);
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  const yesterdayKey = formatIsoDate(y);

  return keys.map((key, index) => {
    const d = parseIsoDate(key);
    const title =
      key === todayKey ? 'Hoy' : key === yesterdayKey ? 'Ayer' : formatSectionDayTitle(d);
    const data = [...(byDay.get(key) ?? [])];
    data.sort((a, b) => {
      const ta = a.closedAt ?? a.openedAt ?? '';
      const tb = b.closedAt ?? b.openedAt ?? '';
      if (a.status === 'open' && b.status !== 'open') return -1;
      if (b.status === 'open' && a.status !== 'open') return 1;
      return tb.localeCompare(ta);
    });
    return { title, data, isFirstSection: index === 0 };
  });
}

export function formatHistoryRangeLabel(fromIso: string, toIso: string): string {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  const o: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  return `${from.toLocaleDateString('es-AR', o)} – ${to.toLocaleDateString('es-AR', o)}`;
}

export function formatTurnoResumenSubtitle(
  sessionSalesCount: number,
  sessionSalesTotal: number,
  todaySalesCount: number
): string {
  if (sessionSalesCount > 0) {
    return `${formatSalesCount(sessionSalesCount)} · ${formatMoney(sessionSalesTotal)}`;
  }
  if (todaySalesCount > 0) {
    return `Sin ventas en el turno · ${formatSalesCount(todaySalesCount)} hoy en la sucursal`;
  }
  return 'Sin ventas en este turno';
}
